/**
 * 65.49's lock tone, played (lockToneMix.ts says what each voice sounds like): one oscillator through a low-pass and a level,
 * and a low-frequency oscillator that either works the level (a rough growl, a beep's square gate) or the pitch (a warble
 * between two tones) at audio rate — so the fast beeps and warbles keep time whatever the frame rate; a slow pulse (the
 * searching ticks) is gated per frame. On the effects bus, not the music's duck stage: it stays readable over gunfire.
 * One instance a sound (the holder's seeker, the ship's warning). Presentation only.
 */
import { getAudioContext, getSfxBus, initAudio } from './sfx';
import { pulseOn, type LockToneFrame } from './lockToneMix';

interface Graph { osc: OscillatorNode; lfo: OscillatorNode; am: GainNode; amDepth: GainNode; fmDepth: GainNode; filter: BiquadFilterNode; level: GainNode }

export class LockTone {
  private g: Graph | null = null;
  private wave: OscillatorType | null = null;
  private lfoSquare: boolean | null = null;

  /** This frame's tone (null: silent — it fades and is let go), at the context's time; `t` the clock its slow pulse keeps. */
  play(frame: LockToneFrame | null, t: number): void {
    if (!frame) { this.release(); return; }
    initAudio();
    const ctx = getAudioContext(), bus = getSfxBus();
    if (!ctx || !bus) return;
    const g = (this.g ??= this.build(ctx, bus)), now = ctx.currentTime;
    if (this.wave !== frame.wave) { g.osc.type = frame.wave; this.wave = frame.wave; }
    const square = !!frame.mod.square;
    if (this.lfoSquare !== square) { g.lfo.type = square ? 'square' : 'sine'; this.lfoSquare = square; }
    const m = frame.mod;
    // the pitch: a warble's centre between its two tones, the LFO swinging it half the span either way
    const span = m.kind === 'fm' ? (m.depth - 1) * frame.hz : 0;
    g.osc.frequency.setTargetAtTime(frame.hz + span / 2, now, 0.03);
    g.fmDepth.gain.setTargetAtTime(span / 2, now, 0.03);
    // the level's modulation: a share `depth` of it swung by the LFO (a square at depth 1: on and off)
    const d = m.kind === 'am' ? Math.max(0, Math.min(1, m.depth)) : 0;
    g.am.gain.setTargetAtTime(1 - d / 2, now, 0.02);
    g.amDepth.gain.setTargetAtTime(d / 2, now, 0.02);
    g.lfo.frequency.setTargetAtTime(Math.max(0.1, m.rate), now, 0.02);
    g.filter.frequency.setTargetAtTime(frame.cutoff, now, 0.03);
    g.level.gain.setTargetAtTime(pulseOn(frame.pulse, t) ? frame.gain : 0, now, 0.005);
  }

  /** Silent now (it fades, then its nodes are let go). */
  release(): void {
    const g = this.g, ctx = getAudioContext();
    if (!g || !ctx) return;
    this.g = null; this.wave = null; this.lfoSquare = null;
    const now = ctx.currentTime;
    g.level.gain.setTargetAtTime(0, now, 0.03);
    for (const o of [g.osc, g.lfo]) { try { o.stop(now + 0.3); } catch { /* stopped */ } }
    setTimeout(() => { try { g.level.disconnect(); } catch { /* gone */ } }, 500);
  }

  private build(ctx: AudioContext, bus: AudioNode): Graph {
    const osc = ctx.createOscillator(), lfo = ctx.createOscillator();
    const am = ctx.createGain(), amDepth = ctx.createGain(), fmDepth = ctx.createGain(), level = ctx.createGain();
    const filter = ctx.createBiquadFilter(); filter.type = 'lowpass'; filter.Q.value = 0.7;
    level.gain.value = 0; amDepth.gain.value = 0; fmDepth.gain.value = 0;
    osc.connect(am); am.connect(filter); filter.connect(level); level.connect(bus);
    lfo.connect(amDepth); amDepth.connect(am.gain);
    lfo.connect(fmDepth); fmDepth.connect(osc.frequency);
    osc.start(); lfo.start();
    return { osc, lfo, am, amDepth, fmDepth, filter, level };
  }
}
